import React, { useState, useMemo, useEffect, useLayoutEffect, useCallback, useRef } from 'react'
import { IS_PERIOD_CONTRACT_SELECTION_DISABLED } from '../../config/featureFlags'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useStateMachine } from 'little-state-machine'
import { updateStore, TermType, storeInitState } from '../../store'
import {
	useUser,
	useServiceConfig,
	useSearchCaddies,
	useProList,
	useTours,
	usePartnerUnavailable,
	useMyContractedTourIds,
} from '../../hooks'
import { useAuthStatus } from '../../utils'
import { XMarkIcon } from '@heroicons/react/24/solid'
import { OfferContractSelect } from '../offers/create/components/OfferContractSelect'
import { TourSelection } from '../../components/TourSelection/TourSelection'
import { AdvancedNavigationGuard } from '../../components/AdvancedNavigationGuard'
import { SearchCaddieCard } from './components/SearchCaddieCard'
import { SearchProCard } from './components/SearchProCard'
import CaddieLoading from './components/CaddieLoading'
import { NoCaddieFound } from './components/NoCaddieFound'
import { InfoBanner } from '../../components/InfoBanner/InfoBanner'
import styles from './SearchMatch.module.scss'

const TAB_CONTRACTS = 'contracts'
const TAB_PARTNER = 'partner'

export const SearchMatch = () => {
	const navigate = useNavigate()
	const location = useLocation()
	const [searchParams, setSearchParams] = useSearchParams()
	const { data: user } = useUser()
	const { actions, state } = useStateMachine({ updateStore })
	const offerInputRef = useRef(state.offerInput)
	offerInputRef.current = state.offerInput
	const [auth] = useAuthStatus()
	const { mode } = useServiceConfig()

	const isPro = user?.userType === 'tourPro'
	const isCaddieView = !isPro
	// 제안하는 쪽 기준: 프로가 제안하면 by=pro(selectedCaddie), 캐디가 제안하면 by=caddie(selectedPro)
	const byParam = isPro ? 'pro' : 'caddie'
	const partnerLabel = isPro ? '캐디' : '프로'

	// 사전계약 "정식계약 전환하기"로 들어온 경우에만 스토어 값 유지, 그 외에는 첫 렌더부터 비움
	const fromPreContract = !!location.state?.fromPreContractId
	const defaultContractType =
		state.offerInput?.offerType || (mode === 'OFFICIAL' ? 'short' : 'preContract')
	const defaultPeriodTypes = (() => {
		const t = state.offerInput?.termType
		if (!t) return []
		if (t === TermType.FULL_YEAR) return ['first_half', 'second_half']
		if (t === TermType.FIRST_HALF || t === TermType.SECOND_HALF) return [t]
		return []
	})()
	const defaultTourIds = state.offerInput?.tourIds || []

	const [activeTab, setActiveTab] = useState(TAB_CONTRACTS)
	const [selectedContractType, setSelectedContractType] = useState(
		fromPreContract ? defaultContractType : mode === 'OFFICIAL' ? 'short' : 'preContract',
	)
	const [selectedPeriodTypes, setSelectedPeriodTypes] = useState(
		fromPreContract ? defaultPeriodTypes : [],
	)
	const [tourIds, setTourIds] = useState(fromPreContract ? defaultTourIds : [])
	// 캐디 전용: 프로 찾기 시 소속(KLPGA/KPGA) 선택 — 계약 유형 위에 선택
	const [caddieTourType, setCaddieTourType] = useState(null) // null | 'KLPGA' | 'KPGA'

	const isOfficialMode = mode === 'OFFICIAL'

	useLayoutEffect(() => {
		if (!isOfficialMode || !IS_PERIOD_CONTRACT_SELECTION_DISABLED) return
		if (selectedContractType !== 'period') return
		setSelectedContractType('short')
		setSelectedPeriodTypes([])
	}, [isOfficialMode, selectedContractType])

	const clearOfferInputStore = useCallback(() => {
		actions.updateStore({
			offerInput: {
				...storeInitState.offerInput,
				selectedCaddie: null,
				selectedPro: null,
				targetUserId: '',
				tourIds: [],
			},
		})
	}, [actions])

	// 새로 진입했을 때(사전계약 제외) 스토어 offerInput 즉시 초기화 — 선택 캐디/프로 제거
	// 주의: 언마운트 시 비우면 안 됨. 제안하기로 /offer/create/terms 이동 시 이 cleanup이
	// targetUserId·contract 등 전체 offerInput을 지워 확인 단계에서 "필수 정보가 없습니다"가 난다.
	useLayoutEffect(() => {
		if (location.state?.fromPreContractId) return
		clearOfferInputStore()
	}, [location.state?.fromPreContractId, clearOfferInputStore])

	// 소속: 프로는 본인 tour_type 고정, 캐디는 선택 가능
	const displayTourType = isPro ? user?.tourPro?.tour_type : caddieTourType
	const tourGender = useMemo(() => {
		if (displayTourType === 'KLPGA') return 'FEMALE'
		if (displayTourType === 'KPGA') return 'MALE'
		return undefined
	}, [displayTourType])
	const { data: toursData } = useTours(tourGender)
	const findTourById = useMemo(() => {
		if (!toursData) return () => null
		return (tourId) => {
			const domestic = toursData.domestic?.find((t) => t.id === tourId)
			if (domestic) return domestic
			if (toursData.international?.tours) {
				const allIntl = Object.values(toursData.international.tours).flat()
				return allIntl.find((t) => t.id === tourId) || null
			}
			return null
		}
	}, [toursData])
	const previousSelectedTours = useMemo(
		() => ({
			tours: (tourIds || []).map((id) => findTourById(id)).filter(Boolean),
		}),
		[tourIds, findTourById],
	)

	useEffect(() => {
		const tab = searchParams.get('tab')
		if (tab === TAB_PARTNER || tab === TAB_CONTRACTS) setActiveTab(tab)
	}, [searchParams])

	// 캐디가 소속(KLPGA/KPGA)을 바꾸면 선택된 프로·대회 전부 초기화
	useEffect(() => {
		if (!isCaddieView) return
		setTourIds([])
		actions.updateStore({
			offerInput: {
				...state.offerInput,
				selectedPro: null,
				targetUserId: '',
				tourIds: [],
			},
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps -- caddieTourType 변경 시에만 초기화
	}, [caddieTourType, isCaddieView])

	// 홈 "프로 찾기" 등에서 tourId 쿼리로 진입 시 해당 대회 미리 선택 (최초 1회만)
	const tourIdParam = searchParams.get('tourId')
	const appliedTourIdFromUrl = useRef(false)
	useEffect(() => {
		if (!tourIdParam || appliedTourIdFromUrl.current) return
		appliedTourIdFromUrl.current = true
		setTourIds([tourIdParam])
		if (mode === 'OFFICIAL') setSelectedContractType('short')
		actions.updateStore({
			offerInput: {
				...state.offerInput,
				tourIds: [tourIdParam],
				...(mode === 'OFFICIAL' ? { offerType: 'short' } : {}),
			},
		})
	}, [tourIdParam, mode])

	const termTypeFromPeriodTypes = useMemo(() => {
		if (selectedContractType !== 'period') return ''
		if (
			selectedPeriodTypes.includes('first_half') &&
			selectedPeriodTypes.includes('second_half')
		)
			return TermType.FULL_YEAR
		if (selectedPeriodTypes.includes('first_half')) return TermType.FIRST_HALF
		if (selectedPeriodTypes.includes('second_half')) return TermType.SECOND_HALF
		return ''
	}, [selectedContractType, selectedPeriodTypes])

	const syncOfferInputContract = useCallback(
		(offerType, termTypeVal, tourIdsArr) => {
			actions.updateStore({
				offerInput: {
					...state.offerInput,
					offerType: offerType || selectedContractType,
					termType:
						termTypeVal !== undefined ? termTypeVal : state.offerInput?.termType || '',
					tourIds: tourIdsArr !== undefined ? tourIdsArr : tourIds,
				},
			})
		},
		[actions, state.offerInput, selectedContractType, tourIds],
	)

	useEffect(() => {
		const term = selectedContractType === 'period' ? termTypeFromPeriodTypes : undefined
		syncOfferInputContract(selectedContractType, term, tourIds)
	}, [selectedContractType, tourIds, termTypeFromPeriodTypes])

	const handleContractTypeChange = (contractType) => {
		setSelectedContractType(contractType)
		if (contractType !== 'period') setSelectedPeriodTypes([])
	}

	const handlePeriodTypeChange = (periodTypes) => {
		setSelectedPeriodTypes(periodTypes)
	}

	const handleTourIdsChange = (ids) => {
		setTourIds(ids || [])
		actions.updateStore({
			offerInput: { ...state.offerInput, tourIds: ids || [] },
		})
	}

	const contractComplete = useMemo(() => {
		if (!selectedContractType) return false
		if (selectedContractType === 'preContract') return true
		if (selectedContractType === 'short') return tourIds.length === 1
		if (selectedContractType === 'long') return tourIds.length >= 2
		if (selectedContractType === 'period') return !!termTypeFromPeriodTypes
		return false
	}, [selectedContractType, tourIds.length, termTypeFromPeriodTypes])

	const selectedPartner = isPro ? state.offerInput?.selectedCaddie : state.offerInput?.selectedPro
	const hasPartner = !!selectedPartner?.id
	const currentYear = new Date().getFullYear()
	const { data: partnerUnavailable } = usePartnerUnavailable(
		selectedPartner?.id ?? null,
		currentYear,
		hasPartner,
	)
	const { data: myContractedTourIds = [] } = useMyContractedTourIds()
	const disabledTourIds = useMemo(() => {
		const partner = partnerUnavailable?.busyTourIds ?? []
		const mine = myContractedTourIds ?? []
		return [...new Set([...partner, ...mine])]
	}, [partnerUnavailable?.busyTourIds, myContractedTourIds])
	const disabledPeriodHalves = partnerUnavailable?.busyPeriodHalves ?? []

	// 계약 체결 대회 ID가 늦게 로드되면, URL 등으로 잡힌 선택을 제거
	useEffect(() => {
		if (!myContractedTourIds?.length) return
		const next = tourIds.filter((id) => !myContractedTourIds.includes(id))
		if (next.length === tourIds.length) return
		setTourIds(next)
		actions.updateStore({
			offerInput: { ...offerInputRef.current, tourIds: next },
		})
	}, [myContractedTourIds, tourIds, actions])

	const canOffer = contractComplete && hasPartner

	const handleOfferClick = () => {
		if (!canOffer) return
		const payload = {
			offerInput: {
				...state.offerInput,
				offerType: selectedContractType,
				termType: termTypeFromPeriodTypes || state.offerInput?.termType,
				tourIds,
				...(isPro
					? { selectedCaddie: selectedPartner, targetUserId: selectedPartner?.id }
					: { selectedPro: selectedPartner, targetUserId: selectedPartner?.id }),
			},
		}
		actions.updateStore(payload)
		if (selectedContractType === 'preContract' && !isOfficialMode) {
			navigate(`/offer/create/confirm?by=${byParam}`)
		} else {
			navigate(`/offer/create/terms?by=${byParam}`)
		}
	}

	const handleSelectPartner = (partner) => {
		actions.updateStore({
			offerInput: {
				...state.offerInput,
				...(isPro
					? { selectedCaddie: partner, targetUserId: partner?.id }
					: { selectedPro: partner, targetUserId: partner?.id }),
			},
		})
	}

	const handleSwitchTab = (tab) => {
		setActiveTab(tab)
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev)
			next.set('tab', tab)
			return next
		})
	}

	// 탭 위에 보여줄 선택 요약 (계약 조건 + 상대방)
	const selectionSummary = useMemo(() => {
		const parts = []
		// 계약 조건
		if (selectedContractType === 'preContract') {
			parts.push({ label: '계약 조건', value: '사전계약' })
		} else if (selectedContractType === 'short') {
			const tour = tourIds?.length ? findTourById(tourIds[0]) : null
			parts.push({
				label: '계약 조건',
				value: tour ? `단기계약 · ${tour.name}` : '단기계약',
			})
		} else if (selectedContractType === 'long') {
			const names = (tourIds || []).map((id) => findTourById(id)?.name).filter(Boolean)
			parts.push({
				label: '계약 조건',
				value: names.length ? `장기계약 · ${names.join(', ')}` : '장기계약',
			})
		} else if (selectedContractType === 'period') {
			const periodStr =
				selectedPeriodTypes?.length === 2
					? '연간'
					: selectedPeriodTypes?.includes('first_half')
						? '상반기'
						: selectedPeriodTypes?.includes('second_half')
							? '하반기'
							: '기간계약'
			parts.push({ label: '계약 조건', value: `기간계약 · ${periodStr}` })
		}
		// 상대방
		if (selectedPartner?.name) {
			parts.push({ label: partnerLabel, value: selectedPartner.name })
		}
		return parts
	}, [
		selectedContractType,
		tourIds,
		selectedPeriodTypes,
		selectedPartner?.name,
		partnerLabel,
		findTourById,
	])

	return (
		<div className={styles.wrapper}>
			<header className={styles.header}>
				<div className={styles.headerText}>
					<span className={styles.headerTitle}>{isPro ? '캐디 찾기' : '프로 찾기'}</span>
					<span className={styles.headerDescription}>
						계약 조건과 {partnerLabel}를 선택한 뒤 제안할 수 있어요
					</span>
				</div>
				<button
					type="button"
					className={styles.closeButton}
					onClick={() => {
						actions.updateStore({ offerInput: storeInitState.offerInput })
						navigate('/home')
					}}
					aria-label="닫기">
					<XMarkIcon className={styles.closeIcon} />
				</button>
			</header>

			{selectionSummary.length > 0 && (
				<div className={styles.selectionSummary}>
					{selectionSummary.map((item, idx) => (
						<div key={idx} className={styles.selectionSummaryItem}>
							<span className={styles.selectionSummaryLabel}>{item.label}</span>
							<span className={styles.selectionSummaryValue}>{item.value}</span>
						</div>
					))}
				</div>
			)}

			<div className={styles.tabBar}>
				<button
					type="button"
					className={activeTab === TAB_CONTRACTS ? styles.tabActive : styles.tab}
					onClick={() => handleSwitchTab(TAB_CONTRACTS)}>
					계약 조건
				</button>
				<button
					type="button"
					className={activeTab === TAB_PARTNER ? styles.tabActive : styles.tab}
					onClick={() => handleSwitchTab(TAB_PARTNER)}>
					{partnerLabel}
				</button>
			</div>

			{activeTab === TAB_CONTRACTS && (
				<div className={styles.tabPanel}>
					{hasPartner && (
						<div className={styles.partnerFirstHint}>
							<p>
								<strong>{selectedPartner?.name}</strong> {partnerLabel}님을(를)
								선택하셨습니다. 아래에서 계약 조건(계약 유형·대회)을 선택해 주세요.
							</p>
							{(disabledTourIds?.length > 0 || disabledPeriodHalves?.length > 0) && (
								<p className={styles.partnerFirstHintDisabled}>
									비활성화된 항목은 본인 또는 선택한 상대방이 해당 대회·기간에
									이미 계약이 있어서 선택할 수 없습니다.
								</p>
							)}
						</div>
					)}
					{!isOfficialMode && (
						<InfoBanner
							title="사전계약으로 2026년 시즌을 미리 준비하세요"
							description={`사전계약은 아직 정식 대회 일정이 확정되기 전에 미리 ${isCaddieView ? '프로와' : '캐디와'} 계약을 맺어두는 서비스입니다. 실력있는 파트너를 미리 확보하세요!`}
						/>
					)}
					{/* 소속: 프로는 본인 소속 고정(변경 불가), 캐디는 선택 가능 — UI 통일 */}
					<div className={styles.tourTypeSection}>
						<div className={styles.tourSectionTitle}>소속</div>
						<div className={styles.tourTypeTabs}>
							<button
								type="button"
								className={
									displayTourType === 'KPGA'
										? styles.tourTypeTabActive
										: styles.tourTypeTab
								}
								onClick={() => !isPro && setCaddieTourType('KPGA')}
								disabled={isPro}
								aria-pressed={displayTourType === 'KPGA'}
								title="KPGA">
								<img
									src="/images/kpga-ci.png"
									alt="KPGA"
									className={styles.tourTypeLogo}
								/>
							</button>
							<button
								type="button"
								className={
									displayTourType === 'KLPGA'
										? styles.tourTypeTabActive
										: styles.tourTypeTab
								}
								onClick={() => !isPro && setCaddieTourType('KLPGA')}
								disabled={isPro}
								aria-pressed={displayTourType === 'KLPGA'}
								title="KLPGA">
								<img
									src="/images/klpga-ci.png"
									alt="KLPGA"
									className={styles.tourTypeLogo}
								/>
							</button>
						</div>
					</div>
					<OfferContractSelect
						onContractTypeChange={handleContractTypeChange}
						onPeriodTypeChange={handlePeriodTypeChange}
						isOfficialMode={isOfficialMode}
						isCaddieView={isCaddieView}
						initialContractType={selectedContractType}
						initialPeriodTypes={selectedPeriodTypes}
						disabledPeriodHalves={disabledPeriodHalves}
					/>
					{(selectedContractType === 'short' || selectedContractType === 'long') &&
						displayTourType && (
							<AdvancedNavigationGuard>
								<div className={styles.tourSection}>
									<div className={styles.tourSectionTitle}>대회 선택</div>
									<TourSelection
										mode="offer"
										TourCount={selectedContractType === 'long'}
										selectOnlyOne={selectedContractType === 'short'}
										onSelectedToursChange={handleTourIdsChange}
										gender={tourGender}
										upcomingOnly={true}
										previousSelectedTours={previousSelectedTours}
										disabledTourIds={disabledTourIds}
									/>
								</div>
							</AdvancedNavigationGuard>
						)}
				</div>
			)}

			{activeTab === TAB_PARTNER && (
				<PartnerTab
					isPro={isPro}
					tourIds={tourIds}
					selectedContractType={selectedContractType}
					termTypeFromPeriodTypes={termTypeFromPeriodTypes}
					selectedPartner={selectedPartner}
					onSelectPartner={handleSelectPartner}
					contractComplete={contractComplete}
					caddieTourType={caddieTourType}
				/>
			)}

			<div className={styles.footer}>
				<button
					type="button"
					className={canOffer ? styles.offerButton : styles.offerButtonDisabled}
					onClick={handleOfferClick}
					disabled={!canOffer}>
					제안하기
				</button>
				{!contractComplete && hasPartner && (
					<>
						<p className={styles.hint}>계약 조건을 먼저 선택해 주세요</p>
						<button
							type="button"
							className={styles.footerTextLink}
							onClick={() => handleSwitchTab(TAB_CONTRACTS)}>
							계약 선택하러 가기
						</button>
					</>
				)}
				{contractComplete && !hasPartner && (
					<>
						<p className={styles.hint}>{partnerLabel}를 선택해 주세요</p>
						<button
							type="button"
							className={styles.footerTextLink}
							onClick={() => handleSwitchTab(TAB_PARTNER)}>
							{partnerLabel} 찾으러 가기
						</button>
					</>
				)}
			</div>
		</div>
	)
}

function PartnerTab({
	isPro,
	tourIds,
	selectedContractType,
	termTypeFromPeriodTypes,
	selectedPartner,
	onSelectPartner,
	contractComplete,
	caddieTourType,
}) {
	const [searchValue, setSearchValue] = useState('')
	const appliedTourIds = tourIds || []

	// 기간계약일 때: 상반기/하반기 중 선택된 구간에 계약 있는 캐디는 제외
	const periodFilter = useMemo(() => {
		if (!isPro || selectedContractType !== 'period' || !termTypeFromPeriodTypes) return null
		const year = new Date().getFullYear()
		if (termTypeFromPeriodTypes === TermType.FULL_YEAR)
			return { periodYear: year, periodHalf: ['first_half', 'second_half'] }
		return {
			periodYear: year,
			periodHalf: [termTypeFromPeriodTypes],
		}
	}, [isPro, selectedContractType, termTypeFromPeriodTypes])

	const { data: caddieList = [], isLoading: caddieLoading } = useSearchCaddies(
		{
			onlyTour: false,
			tournamentIds: appliedTourIds,
			...(periodFilter && {
				periodYear: periodFilter.periodYear,
				periodHalf: periodFilter.periodHalf,
			}),
		},
		!!isPro,
	)
	const { data: proList = [], isLoading: proLoading } = useProList(
		isPro ? { keyword: '' } : { keyword: searchValue, tournamentIds: appliedTourIds },
		!isPro,
	)

	const loading = isPro ? caddieLoading : proLoading
	// 캐디일 때: 소속(caddieTourType)에 맞는 프로만 표시
	const list = useMemo(() => {
		if (isPro) return caddieList
		if (!caddieTourType) return []
		return (proList || []).filter((p) => p?.user_tour_pro?.tour_type === caddieTourType)
	}, [isPro, caddieList, proList, caddieTourType])

	const filteredList = useMemo(() => {
		if (!list.length) return []
		if (isPro) return list
		const filtered = list.filter(
			(p) =>
				!searchValue || (p?.name || '').toLowerCase().includes(searchValue.toLowerCase()),
		)
		// 프로: 출전 예정 → 출전 여부 미확정 → 그 외 순으로 상단 정렬 (백엔드와 동일 문자열)
		const order = (p) => {
			if (p?.attendance_status === '출전 예정') return 0
			if (p?.attendance_status === '출전 여부 미확정') return 1
			return 2
		}
		return [...filtered].sort((a, b) => order(a) - order(b))
	}, [list, isPro, searchValue])

	if (loading) {
		return (
			<div className={styles.tabPanel}>
				<CaddieLoading />
			</div>
		)
	}

	return (
		<div className={styles.tabPanel}>
			{!contractComplete && (
				<p className={styles.partnerHint}>
					계약 조건을 먼저 선택하면 맞는 {isPro ? '캐디' : '프로'}만 볼 수 있어요 (선택
					없으면 전체 목록)
				</p>
			)}
			{isPro ? (
				filteredList.length === 0 ? (
					<NoCaddieFound />
				) : (
					<div className={styles.cardGrid}>
						{filteredList.map((caddie) => (
							<div key={caddie.id} className={styles.cardWrap}>
								<SearchCaddieCard
									caddie={caddie}
									selectionMode
									selected={selectedPartner?.id === caddie.id}
									onSelect={() => onSelectPartner(caddie)}
								/>
							</div>
						))}
					</div>
				)
			) : (
				<>
					{!caddieTourType ? (
						<div className={styles.noResult}>
							소속(KPGA/KLPGA)을 먼저 선택해 주세요.
						</div>
					) : filteredList.length === 0 ? (
						<div className={styles.noResult}>
							{appliedTourIds.length > 0
								? '선택한 대회에 계약이 없는 프로가 없어요.'
								: '검색 결과가 없어요.'}
						</div>
					) : (
						<div className={styles.cardGrid}>
							{filteredList.map((pro) => (
								<div key={pro.id} className={styles.cardWrap}>
									<SearchProCard
										pro={pro}
										attendanceLabel={pro.attendance_status}
										selectionMode
										selected={selectedPartner?.id === pro.id}
										onSelect={() => onSelectPartner(pro)}
									/>
								</div>
							))}
						</div>
					)}
				</>
			)}
		</div>
	)
}

export default SearchMatch
